<template>
  <v-layout>
    <v-autocomplete
      :items="availableClients"
      :disabled="!editable"
      v-model="form.newClientId"
      item-value="id"
      item-text="title"
      no-data-text="No clients"
      style="width: 400px; max-width: 400px"
      label="Please, select client"
      persistent-hint
      @input="handleCaseClientChange"
      :loading="changeClientInProgress"
    />

    <v-layout row justify-center v-if="showModal">
      <v-dialog v-model="showModal" persistent max-width="600">
        <v-card>
          <v-card-title class="headline">Change client confirmation</v-card-title>

          <v-card-text>
            <p>Please, confirm case client change. The following changes will be performed:</p>
            <div class="mt-2"><b>Client:</b> {{currentClientTitle | NoEmpty}}</div>
            <div class="mt-2"><b>New client:</b> {{newClientTitle | NoEmpty}}</div>
            <div v-if="changeClientAvailableServiceIds.length > 0" class="mt-2">
              <b>Services will be transferred automatically:</b>
              <ul>
                <li class="mt-1" v-for="changeClientAvailableService in changeClientAvailableServices"
                    :key="changeClientAvailableService.id">
                  ({{changeClientAvailableService.code}}) {{ changeClientAvailableService.title }}
                </li>
              </ul>
            </div>
            <div v-if="changeClientOrphanServiceIds.length > 0" class="mt-2">
              <b>Services will not be transferred automatically, check services in the case:</b>
              <ul>
                <li v-for="changeClientOrphanService in changeClientOrphanServices" :key="changeClientOrphanService.id">
                  ({{changeClientOrphanService.code}}) {{ changeClientOrphanService.title }}
                </li>
              </ul>
            </div>
            <div class="mt-2">
              <b>Client specialists will be deleted!</b>
            </div>

            <div v-if="dryRunResult">
              <div v-if="draftInvoices.length > 0">
                <b>Case will be detached from draft invoices:</b>
                <span v-for="invoice in draftInvoices" style="margin-right: 1em">
                  <router-link :to="{name: 'InvoicePage', params: {invoiceId: invoice.id}}">
                    {{invoice.id}}
                  </router-link>
                </span>
              </div>

              <div v-if="chargedInvoices.length > 0" style="color: red">
                <b>Case can't be transferred, it attached to charged invoice:</b>
                <span v-for="invoice in chargedInvoices">
                  <router-link :to="{name: 'InvoicePage', params: {invoiceId: invoice.id}}">
                    {{invoice.id}}
                  </router-link>
                </span>
              </div>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer/>

            <v-btn
              color="primary darken-1"
              flat="flat"
              :loading="!isSubmitEnabled"
              :disabled="chargedInvoices.length > 0"
              @click="handleFormSubmit"
            >
              OK
            </v-btn>

            <v-btn
              color="gray darken-1"
              flat="flat"
              @click="handleCancelChangeClientClick"
            >
              Cancel
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-layout>
  </v-layout>
</template>

<script>
  import NoEmpty from '../Filters/NoEmpty.js'
  import {mapActions, mapState} from "vuex";
  import api from "../../api";
  import _ from "lodash";
  import {notificationService} from "../../application";

  function getDefaultFormData(currentClientId) {
    return {
      newClientId: currentClientId
    }
  }

  function getById(list, id) {
    return _.find(list, item => {
      return item.id === id
    });
  }

  export default {
    name: 'change-case-client-form',
    components: {},
    filters: {
      NoEmpty
    },
    props: {
      caseId: {
        type: String,
        required: true
      },
      currentClientId: {
        type: String,
        required: true
      },
      editable: {
        type: Boolean,
        required: true
      }
    },
    data() {
      return {
        form: getDefaultFormData(this.currentClientId),
        showModal: false,
        isValid: true,
        requestInProgress: false,
        changeClientInProgress: false,
        dryRunResult: null,
        changeClientAvailableServiceIds: [],
        changeClientOrphanServiceIds: []
      }
    },
    computed: {
      ...mapState({
        availableClients: 'availableClients',
        availableServices: 'availableServices',
      }),
      currentClientTitle() {
        let client = getById(this.availableClients, this.currentClientId);
        return _.get(client, 'title', null)
      },
      newClientTitle() {
        let newClient = getById(this.availableClients, this.form.newClientId);
        return _.get(newClient, 'title', null)
      },
      changeClientAvailableServices() {
        let result = [];
        for (let id of this.changeClientAvailableServiceIds) {
          const service = getById(this.availableServices, id);

          if (!service) {
            continue
          }

          result.push(service)
        }

        return result
      },
      changeClientOrphanServices() {
        let result = [];
        for (let id of this.changeClientOrphanServiceIds) {
          const service = getById(this.availableServices, id);

          if (!service) {
            continue
          }

          result.push(service)
        }

        return result
      },
      isSubmitEnabled() {
        const valid = this.isValid && !this.requestInProgress;

        if (!valid) {
          return false
        }

        if (!this.dryRunResult) {
          return false
        }

        return true
      },
      draftInvoices() {
        if (!this.dryRunResult) {
          return []
        }

        return this.dryRunResult.caseAttachedToInvoicesInfo.invoices.filter(it => it.type === 'AttachedToDraft')
      },
      chargedInvoices() {
        if (!this.dryRunResult) {
          return []
        }

        return this.dryRunResult.caseAttachedToInvoicesInfo.invoices.filter(it => it.type === 'AttachedToCharged')
      }
    },
    mounted() {
      this.fetchInitDataIfRequired()
    },
    watch: {
      showModal() {
        if (!this.showModal) {
          this.resetForm()
        }
      }
    },
    methods: {
      ...mapActions({
        fetchInitDataIfRequired: 'fetchInitDataIfRequired',
        runRequest: 'caseChangeCaseClient'
      }),
      async handleCaseClientChange(value) {
        this.changeClientInProgress = true;

        const result = await api.changeCaseClient(this.caseId, value, true);

        this.changeClientAvailableServiceIds = _.get(result, 'availableServiceIds', []);
        this.changeClientOrphanServiceIds = _.get(result, 'orphanServiceIds', []);
        this.dryRunResult = result;

        this.showModal = true
      },
      handleHideForm() {
        this.showModal = false
      },
      handleCancelChangeClientClick() {
        this.form.newClientId = this.currentClientId;
        this.clearState()
      },
      async handleFormSubmit() {
        if (!this.isSubmitEnabled) {
          return
        }

        this.requestInProgress = true;
        const result = await this.runRequest({
          caseId: this.caseId,
          newClientId: this.form.newClientId,
        })
          .then(res => {
            this.requestInProgress = false;
            notificationService.info('Operation success');
            return res
          })
          .catch(e => {
            this.requestInProgress = false;
            notificationService.error('Operation failed');
            throw e
          });

        this.$emit('caseUpdated', result);
        this.clearState()
      },
      clearState() {
        this.dryRunResult = null;
        this.changeClientAvailableServiceIds = [];
        this.changeClientOrphanServiceIds = [];
        this.changeClientInProgress = false;
        this.showModal = false
      },
      resetForm() {
        this.form = getDefaultFormData(this.currentClientId)
      },
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/Sales/ChangeCaseClientForm.vue